import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle,
  Clock,
  Users,
  Bell,
  Plus,
  Trash2,
  Save,
  ChevronRight,
  ShieldAlert,
  Smartphone,
  Mail,
  Phone,
  ArrowRight,
  UserPlus
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { cn } from './ui/utils';
import EscalationVoiceRecorder from './EscalationVoiceRecorder';
import { fetchUsers } from '../utils/firebase/api';
import { getCompanyId } from '../utils/tenantContext';
import { doc, setDoc } from 'firebase/firestore';
import { db, auth } from '../utils/firebase/client';

// Types for the Escalation Matrix
type EscalationLevel = {
  id: string;
  level: number;
  delayMinutes: number;
  notifyRoles: string[];
  notifyUsers: string[]; // User IDs
  channels: ('sms' | 'email' | 'push' | 'voice')[];
  recordingUrl?: string;
  requireAck: boolean;
};

type EscalationRule = {
  id: string;
  companyId?: string;
  name: string;
  description: string;
  triggerType: 'alert_severity' | 'missed_checkin' | 'parameter_breach' | 'device_offline';
  severity: 'critical' | 'high' | 'medium' | 'low';
  isActive: boolean;
  levels: EscalationLevel[];
};

const AVAILABLE_ROLES = ['Pumper', 'Field Supervisor', 'Operations Manager', 'Safety Officer', 'Admin'];

interface EscalationMatrixProps {
  isDarkMode?: boolean;
}

export default function EscalationMatrix({ isDarkMode = false }: EscalationMatrixProps) {
  const [users, setUsers] = useState<any[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const companyId = getCompanyId();
        const fetchedUsers = await fetchUsers(companyId);
        setUsers(fetchedUsers);
      } catch (error) {
        console.error('Error loading users:', error);
        toast.error('Failed to load users');
      } finally {
        setLoadingUsers(false);
      }
    };
    loadUsers();
  }, []);

  const [rules, setRules] = useState<EscalationRule[]>([
    {
      id: 'r1',
      name: 'Critical Pressure Breach',
      description: 'Escalation path for sustained high pressure events > 5000 PSI',
      triggerType: 'parameter_breach',
      severity: 'critical',
      isActive: true,
      levels: [
        {
          id: 'l1',
          level: 1,
          delayMinutes: 0,
          notifyRoles: ['Pumper'],
          notifyUsers: [],
          channels: ['push', 'sms'],
          requireAck: true
        },
        {
          id: 'l2',
          level: 2,
          delayMinutes: 15,
          notifyRoles: ['Field Supervisor'],
          notifyUsers: [],
          channels: ['sms', 'email', 'voice'],
          requireAck: true
        },
        {
          id: 'l3',
          level: 3,
          delayMinutes: 45,
          notifyRoles: ['Operations Manager'],
          notifyUsers: [],
          channels: ['voice', 'email'],
          requireAck: true
        }
      ]
    }
  ]);

  const [selectedRuleId, setSelectedRuleId] = useState<string>(rules[0].id);

  const selectedRule = rules.find(r => r.id === selectedRuleId) || rules[0];

  const handleAddLevel = () => {
    const newLevel: EscalationLevel = {
      id: Math.random().toString(36).substr(2, 9),
      level: selectedRule.levels.length + 1,
      delayMinutes: 30,
      notifyRoles: [],
      notifyUsers: [],
      channels: ['email'],
      requireAck: true
    };

    const updatedRules = rules.map(r => {
      if (r.id === selectedRuleId) {
        return { ...r, levels: [...r.levels, newLevel] };
      }
      return r;
    });

    setRules(updatedRules);
  };

  const handleRemoveLevel = (levelId: string) => {
    const updatedRules = rules.map(r => {
      if (r.id === selectedRuleId) {
        return { ...r, levels: r.levels.filter(l => l.id !== levelId) };
      }
      return r;
    });
    setRules(updatedRules);
  };

  const updateLevel = (levelId: string, updates: Partial<EscalationLevel>) => {
    const updatedRules = rules.map(r => {
      if (r.id === selectedRuleId) {
        return {
          ...r,
          levels: r.levels.map(l => l.id === levelId ? { ...l, ...updates } : l)
        };
      }
      return r;
    });
    setRules(updatedRules);
  };

  const handleSaveRule = async () => {
    try {
      const companyId = getCompanyId();
      if (!companyId) throw new Error('No tenant ID found');

      const ruleRef = doc(db, `companies/${companyId}/escalation_rules`, selectedRule.id);
      await setDoc(ruleRef, {
        ...selectedRule,
        companyId,
        updatedAt: new Date().toISOString(),
        updatedBy: auth.currentUser?.uid
      });

      toast.success('Escalation rule saved successfully');
    } catch (error) {
      console.error('Error saving rule:', error);
      toast.error('Failed to save escalation rule');
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'sms': return <Smartphone className="w-4 h-4" />;
      case 'email': return <Mail className="w-4 h-4" />;
      case 'voice': return <Phone className="w-4 h-4" />;
      case 'push': return <Bell className="w-4 h-4" />;
      default: return <Bell className="w-4 h-4" />;
    }
  };

  return (
    <div className={`min-h-screen p-6 space-y-6 transition-colors duration-300 ${isDarkMode ? 'bg-zinc-950' : 'bg-slate-50/50'}`}>
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
            <ShieldAlert className={`w-8 h-8 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
            Escalation Matrix
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Configure automated alert escalation chains and notification policies</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" className="bg-white dark:bg-zinc-900 dark:text-white dark:border-zinc-700 dark:hover:bg-zinc-800">
            <Clock className="w-4 h-4 mr-2" />
            History Log
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white shadow-lg shadow-blue-500/20">
            <Plus className="w-4 h-4 mr-2" />
            New Policy
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Sidebar - Policy List */}
        <div className="lg:col-span-3 space-y-4">
          <Card className="border-white/20 dark:border-zinc-800 shadow-xl bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl h-full rounded-[1.4rem]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">Active Policies</CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              <div className="space-y-1">
                {rules.map(rule => (
                  <button
                    key={rule.id}
                    onClick={() => setSelectedRuleId(rule.id)}
                    className={cn(
                      "w-full text-left p-3 rounded-xl text-sm font-medium transition-all flex items-center justify-between group",
                      selectedRuleId === rule.id
                        ? "bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border border-blue-100 dark:border-blue-800"
                        : "text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-zinc-800 border border-transparent"
                    )}
                  >
                    <div className="flex items-center gap-3">
                      <div className={cn(
                        "w-2 h-2 rounded-full shadow-sm",
                        rule.isActive ? "bg-emerald-500 shadow-emerald-500/50" : "bg-slate-300 dark:bg-slate-600"
                      )} />
                      {rule.name}
                    </div>
                    <ChevronRight className={cn(
                      "w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity",
                      selectedRuleId === rule.id && "opacity-100"
                    )} />
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content - Matrix Editor */}
        <div className="lg:col-span-9 space-y-6">
          {/* Policy Config Header */}
          <Card className="border-white/20 dark:border-zinc-800 shadow-xl bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl rounded-[1.4rem]">
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-xl font-semibold text-slate-900 dark:text-white">{selectedRule.name}</h2>
                    <Badge variant={selectedRule.isActive ? 'default' : 'secondary'} className={selectedRule.isActive ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 hover:bg-emerald-100' : 'dark:bg-zinc-800 dark:text-zinc-400'}>
                      {selectedRule.isActive ? 'Active' : 'Draft'}
                    </Badge>
                    <Badge variant="outline" className="uppercase text-xs font-bold tracking-wider dark:border-zinc-700 dark:text-zinc-400">
                      {selectedRule.severity}
                    </Badge>
                  </div>
                  <p className="text-slate-500 dark:text-slate-400">{selectedRule.description}</p>
                </div>
                <Button variant="ghost" size="icon" onClick={handleSaveRule} className="hover:bg-slate-100 dark:hover:bg-zinc-800">
                  <Save className="w-4 h-4 text-slate-400 dark:text-slate-500" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-slate-100 dark:border-zinc-800">
                <div>
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Trigger Condition</label>
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded-xl border border-slate-100 dark:border-zinc-800">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <span className="text-sm font-medium capitalize">{selectedRule.triggerType.replace('_', ' ')}</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">SLA Target</label>
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded-xl border border-slate-100 dark:border-zinc-800">
                    <Clock className="w-4 h-4 text-blue-500" />
                    <span className="text-sm font-medium">15 Minutes</span>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2 block">Total Levels</label>
                  <div className="flex items-center gap-2 text-slate-700 dark:text-slate-300 bg-slate-50 dark:bg-zinc-800/50 p-2 rounded-xl border border-slate-100 dark:border-zinc-800">
                    <Users className="w-4 h-4 text-purple-500" />
                    <span className="text-sm font-medium">{selectedRule.levels.length} Escalation Steps</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Escalation Levels Visualizer */}
          <div className="relative space-y-4">
            <div className="absolute left-8 top-8 bottom-8 w-0.5 bg-slate-200 dark:bg-zinc-800 -z-10" />

            {selectedRule.levels.map((level, index) => (
              <motion.div
                key={level.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-white/20 dark:border-zinc-800 shadow-lg hover:shadow-xl transition-all relative overflow-hidden bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl rounded-[1.4rem]">
                  <div className={cn(
                    "absolute left-0 top-0 bottom-0 w-1",
                    index === 0 ? "bg-red-500" : index === 1 ? "bg-orange-500" : "bg-amber-500"
                  )} />

                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row gap-6">
                      {/* Level Indicator */}
                      <div className="flex-shrink-0 flex flex-col items-center gap-2">
                        <div className={cn(
                          "w-16 h-16 rounded-2xl flex items-center justify-center text-2xl font-bold text-white shadow-lg",
                          index === 0 ? "bg-gradient-to-br from-red-500 to-red-600" : index === 1 ? "bg-gradient-to-br from-orange-500 to-orange-600" : "bg-gradient-to-br from-amber-500 to-amber-600"
                        )}>
                          L{level.level}
                        </div>
                        <div className="text-xs font-medium text-slate-500 dark:text-slate-400 bg-white dark:bg-zinc-800 px-2 py-1 rounded-full border border-slate-200 dark:border-zinc-700 shadow-sm">
                          +{level.delayMinutes}m
                        </div>
                      </div>

                      {/* Configuration */}
                      <div className="flex-1 space-y-4">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-slate-900 dark:text-white">Escalation Level {level.level}</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">
                              {index === 0 ? 'Immediate notification triggered upon event detection' : `Triggered if not acknowledged within ${level.delayMinutes} minutes`}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => handleRemoveLevel(level.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Recipients */}
                          <div className="space-y-4">
                            <div className="space-y-2">
                              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Notify Roles</label>
                              <div className="flex flex-wrap gap-2">
                                {level.notifyRoles.map(role => (
                                  <Badge key={role} variant="secondary" className="bg-slate-100 dark:bg-zinc-800 text-slate-700 dark:text-slate-300 border-slate-200 dark:border-zinc-700">
                                    {role}
                                    <button
                                      className="ml-1 hover:text-red-500"
                                      onClick={() => {
                                        const newRoles = level.notifyRoles.filter(r => r !== role);
                                        updateLevel(level.id, { notifyRoles: newRoles });
                                      }}
                                    >
                                      &times;
                                    </button>
                                  </Badge>
                                ))}
                                <div className="relative group">
                                  <Button variant="outline" size="sm" className="h-6 text-xs border-dashed dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800">
                                    <Plus className="w-3 h-3 mr-1" /> Add Role
                                  </Button>
                                  <div className="absolute top-full left-0 mt-1 w-48 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 rounded-md shadow-lg hidden group-hover:block z-10">
                                    {AVAILABLE_ROLES.filter(r => !level.notifyRoles.includes(r)).map(role => (
                                      <button
                                        key={role}
                                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-zinc-800 dark:text-slate-300"
                                        onClick={() => {
                                          updateLevel(level.id, { notifyRoles: [...level.notifyRoles, role] });
                                        }}
                                      >
                                        {role}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Notify Specific Users</label>
                              <div className="flex flex-wrap gap-2">
                                {level.notifyUsers.map(userId => {
                                  const user = users.find(u => u.id === userId);
                                  return (
                                    <Badge key={userId} variant="secondary" className="bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800">
                                      {user?.name || user?.email || 'Unknown User'}
                                      <button
                                        className="ml-1 hover:text-red-500"
                                        onClick={() => {
                                          const newUsers = level.notifyUsers.filter(id => id !== userId);
                                          updateLevel(level.id, { notifyUsers: newUsers });
                                        }}
                                      >
                                        &times;
                                      </button>
                                    </Badge>
                                  );
                                })}
                                <div className="relative group">
                                  <Button variant="outline" size="sm" className="h-6 text-xs border-dashed dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-800">
                                    <UserPlus className="w-3 h-3 mr-1" /> Add User
                                  </Button>
                                  <div className="absolute top-full left-0 mt-1 w-64 bg-white dark:bg-zinc-900 border border-slate-200 dark:border-zinc-700 rounded-md shadow-lg hidden group-hover:block z-10 max-h-60 overflow-y-auto">
                                    {loadingUsers ? (
                                      <div className="p-2 text-xs text-slate-500 dark:text-slate-400">Loading users...</div>
                                    ) : (
                                      users.filter(u => !level.notifyUsers.includes(u.id)).map(user => (
                                        <button
                                          key={user.id}
                                          className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 dark:hover:bg-zinc-800 dark:text-slate-300 truncate"
                                          onClick={() => {
                                            updateLevel(level.id, { notifyUsers: [...level.notifyUsers, user.id] });
                                          }}
                                        >
                                          {user.name || user.email}
                                        </button>
                                      ))
                                    )}
                                    {!loadingUsers && users.length === 0 && (
                                      <div className="p-2 text-xs text-slate-500 dark:text-slate-400">No users found</div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Channels */}
                          <div className="space-y-2">
                            <label className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Notification Channels</label>
                            <div className="flex gap-2">
                              {['push', 'sms', 'email', 'voice'].map(channel => (
                                <button
                                  key={channel}
                                  onClick={() => {
                                    const newChannels = level.channels.includes(channel as any)
                                      ? level.channels.filter(c => c !== channel)
                                      : [...level.channels, channel as any];
                                    updateLevel(level.id, { channels: newChannels });
                                  }}
                                  className={cn(
                                    "p-2 rounded-lg border transition-all",
                                    level.channels.includes(channel as any)
                                      ? "bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-600 dark:text-blue-400"
                                      : "bg-white dark:bg-zinc-800 border-slate-200 dark:border-zinc-700 text-slate-400 dark:text-slate-500 hover:border-slate-300 dark:hover:border-zinc-600"
                                  )}
                                  title={channel.toUpperCase()}
                                >
                                  {getChannelIcon(channel)}
                                </button>
                              ))}
                            </div>

                            {/* Voice Recorder */}
                            {level.channels.includes('voice') && (
                              <EscalationVoiceRecorder
                                existingUrl={level.recordingUrl}
                                onRecordingComplete={(url) => updateLevel(level.id, { recordingUrl: url })}
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Connector Arrow */}
                {index < selectedRule.levels.length - 1 && (
                  <div className="flex justify-center py-2">
                    <div className="bg-slate-100 dark:bg-zinc-800 p-1 rounded-full border border-slate-200 dark:border-zinc-700">
                      <ArrowRight className="w-4 h-4 text-slate-400 dark:text-slate-500 rotate-90" />
                    </div>
                  </div>
                )}
              </motion.div>
            ))}

            {/* Add Level Button */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex justify-center pt-4"
            >
              <Button
                variant="outline"
                className="border-dashed border-2 border-slate-300 dark:border-zinc-700 text-slate-500 dark:text-slate-400 hover:border-blue-500 dark:hover:border-blue-500 hover:text-blue-600 dark:hover:text-blue-400 w-full max-w-md h-12 bg-transparent"
                onClick={handleAddLevel}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Escalation Level {selectedRule.levels.length + 1}
              </Button>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}
